import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setChosenPolicy } from '@/model/ltcFinancialPlanningModel';
import { PaymentOptionSection } from '@client/components/ltc-financial-planning/PaymentOptionSection';
import { QuoteLoadingSequence } from '@client/components/ltc-financial-planning/QuoteLoadingSequence';
import { ShopInsuranceModal } from '@client/components/ShopInsuranceModal';
import { Button } from '@client/components/ui/button';
import { useLtcPlanningQuotesByPaymentOption } from '@client/hooks/useLtcPlanningQuotesByPaymentOption';
import { Quote } from '@client/model/quotesModel';
import { selectQuotes } from '@client/model/selectors';

import { StepContentProps } from "../types";

export function PoliciesStep({ navigation }: StepContentProps) {
  const dispatch = useDispatch();
  const [expandedQuoteId, setExpandedQuoteId] = useState<string | null>(null);
  const [isShopInsuranceModalOpen, setIsShopInsuranceModalOpen] = useState(false);
  const [showLoadingSequence, setShowLoadingSequence] = useState(true);
  const existingQuotes = useSelector(selectQuotes);

  // Fetch quotes grouped by payment option
  const { paymentOptionGroups, isLoading, hasAnyQuotes, totalCount } =
    useLtcPlanningQuotesByPaymentOption({
      enabled: true,
      topN: 3,
    });

  const handleSelectQuote = (quote: Quote) => {
    // Quote extends PolicyQuote with ROI-related fields
    dispatch(setChosenPolicy(quote as PolicyQuote));
    navigation.goNext();
  };

  const handleAdjustSelection = () => {
    navigation.goToStep('policy-types');
  };

  const handleToggleExpand = (quoteId: string) => {
    setExpandedQuoteId(expandedQuoteId === quoteId ? null : quoteId);
  };

  if (showLoadingSequence) {
    return (
      <QuoteLoadingSequence
        isDataLoaded={!isLoading}
        onComplete={() => setShowLoadingSequence(false)}
      />
    );
  }

  // Show empty state if no quotes
  if (!hasAnyQuotes) {
    return (
      <div className="py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">No Quotes Available</h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-gray-700">
            No quotes match your selected policy types. Please adjust your selection.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-4">
          <Button
            variant="outline"
            onClick={handleAdjustSelection}
          >
            Adjust Selection
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Top Policy Options for You</h1>
        <p className="mx-auto mt-3 max-w-2xl text-base text-gray-700">
          Here are the top-rated options organized by payment type. Options marked as "Best Match"
          or "Good Match" align with your payment preferences.
        </p>
      </div>

      {/* Payment Option Sections */}
      <div className="mx-auto mt-12 flex w-full max-w-5xl flex-col gap-12">
        {paymentOptionGroups.map(group => (
          <PaymentOptionSection
            key={group.paymentOption}
            group={group}
            expandedQuoteId={expandedQuoteId}
            onToggleExpand={handleToggleExpand}
            onSelectQuote={handleSelectQuote}
            testIdPrefix="ltc-planning"
          />
        ))}
      </div>

      {/* Quote Count Info */}
      <div className="mx-auto mt-6 max-w-5xl text-center text-sm text-gray-700">
        Showing top 3 quotes per payment type from {totalCount} total available quotes
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex items-center justify-center gap-4">
        <Button
          variant="outline"
          onClick={handleAdjustSelection}
        >
          Adjust Selection
        </Button>
        <Button
          variant="secondary"
          onClick={() => setIsShopInsuranceModalOpen(true)}
        >
          Explore Alternative Quotes
        </Button>
      </div>

      <ShopInsuranceModal
        open={isShopInsuranceModalOpen}
        onClose={() => setIsShopInsuranceModalOpen(false)}
        queryOptions={{ skipAutoFetch: existingQuotes.length > 0 }}
      />
    </div>
  );
}
